import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Orbit, Upload, Trash2, Check } from 'lucide-react';
import { compressImage, uid } from '../lib/model';
import { portalRoot } from './TourGuide';

export const openPano = () => window.dispatchEvent(new Event('giss-pano'));
const D = Math.PI / 180;
// lon/lat (°) of a view direction; lon 0..360 maps to the image width, lat +90..-90 to its height
export const panoXY = (mk) => [(((mk.lon % 360) + 360) % 360) / 360, (90 - mk.lat) / 180];
const dir = (lon, lat, r = 1) => [r * Math.cos(lat * D) * Math.cos(lon * D), r * Math.sin(lat * D), r * Math.cos(lat * D) * Math.sin(lon * D)];

// 360° room photo (equirectangular): drag to look around, pinch / wheel to zoom, tap to set a measuring point
function PanoView({ pano, onTap, pending }) {
  const box = useRef(null), st = useRef({ lon: 0, lat: 0, fov: 75 }), api = useRef(null);
  const [, tick] = useState(0);
  useEffect(() => {
    let alive = true, raf = 0, ren = null;
    import('three').then((T) => {
      if (!alive) return;
      const el = box.current, W = () => el.clientWidth, H = () => el.clientHeight;
      ren = new T.WebGLRenderer({ antialias: true }); ren.setPixelRatio(Math.min(2, window.devicePixelRatio)); ren.setSize(W(), H());
      el.prepend(ren.domElement);
      const scene = new T.Scene(), cam = new T.PerspectiveCamera(75, W() / H(), 0.1, 1000);
      const geo = new T.SphereGeometry(500, 64, 40); geo.scale(-1, 1, 1);
      const tex = new T.TextureLoader().load(pano.src); tex.colorSpace = T.SRGBColorSpace;
      scene.add(new T.Mesh(geo, new T.MeshBasicMaterial({ map: tex })));
      const v = new T.Vector3();
      api.current = {
        screen: (mk) => { v.set(...dir(mk.lon, mk.lat, 10)).project(cam); return v.z < 1 && Math.abs(v.x) < 1.05 && Math.abs(v.y) < 1.05 ? [((v.x + 1) / 2) * W(), ((1 - v.y) / 2) * H()] : null; },
        pick: (x, y) => { const r = new T.Raycaster(); r.setFromCamera(new T.Vector2((x / W()) * 2 - 1, -(y / H()) * 2 + 1), cam); const d = r.ray.direction; return { lon: Math.atan2(d.z, d.x) / D, lat: Math.asin(Math.max(-1, Math.min(1, d.y))) / D }; },
      };
      const loop = () => {
        const s = st.current; s.lat = Math.max(-85, Math.min(85, s.lat));
        cam.fov = s.fov; cam.aspect = W() / H(); cam.updateProjectionMatrix(); ren.setSize(W(), H(), false);
        cam.lookAt(...dir(s.lon, s.lat, 500)); ren.render(scene, cam); tick((n) => (n + 1) % 1e6);
        raf = requestAnimationFrame(loop);
      };
      loop();
    });
    return () => { alive = false; cancelAnimationFrame(raf); if (ren) { ren.dispose(); ren.domElement.remove(); } api.current = null; };
  }, [pano.src]);
  const ptrs = useRef(new Map()), g = useRef(null);
  const down = (e) => { e.currentTarget.setPointerCapture(e.pointerId); ptrs.current.set(e.pointerId, [e.clientX, e.clientY]); g.current = { x: e.clientX, y: e.clientY, moved: 0, ...st.current, d: null }; };
  const move = (e) => {
    if (!ptrs.current.has(e.pointerId) || !g.current) return;
    ptrs.current.set(e.pointerId, [e.clientX, e.clientY]);
    const s = st.current, P = [...ptrs.current.values()];
    if (P.length === 2) { const d = Math.hypot(P[0][0] - P[1][0], P[0][1] - P[1][1]); if (g.current.d == null) g.current.d = [d, s.fov]; s.fov = Math.max(30, Math.min(100, g.current.d[1] * (g.current.d[0] / d))); g.current.moved = 99; return; }
    const dx = e.clientX - g.current.x, dy = e.clientY - g.current.y, k = s.fov / box.current.clientHeight;
    g.current.moved = Math.max(g.current.moved, Math.hypot(dx, dy));
    s.lon = g.current.lon - dx * k; s.lat = g.current.lat + dy * k;
  };
  const up = (e) => {
    ptrs.current.delete(e.pointerId);
    if (g.current && g.current.moved < 6 && !ptrs.current.size && api.current) { const r = box.current.getBoundingClientRect(); onTap(api.current.pick(e.clientX - r.left, e.clientY - r.top)); }
    if (!ptrs.current.size) g.current = null;
  };
  const marks = [...(pano.marks || []), ...(pending ? [{ ...pending, id: 'new', text: '+' }] : [])];
  return (
    <div className="pn-view" ref={box} data-testid="pano-view" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onWheel={(e) => { st.current.fov = Math.max(30, Math.min(100, st.current.fov + e.deltaY * 0.05)); }}>
      {api.current && marks.map((mk, i) => { const p = api.current.screen(mk); return p && <span key={mk.id} className={`pn-mark ${mk.id === 'new' ? 'new' : ''}`} style={{ left: p[0], top: p[1] }} data-testid={`pano-mark-${mk.id}`}><b>{mk.id === 'new' ? '+' : i + 1}</b>{mk.id !== 'new' && <small>{mk.text}</small>}</span>; })}
    </div>
  );
}

export default function Pano360({ t, project, updateProject }) {
  const [on, setOn] = useState(false);
  const [cur, setCur] = useState(null);
  const [pend, setPend] = useState(null);
  const [txt, setTxt] = useState('');
  const file = useRef(null);
  useEffect(() => { const h = () => setOn(true); window.addEventListener('giss-pano', h); return () => window.removeEventListener('giss-pano', h); }, []);
  if (!on) return null;
  const list = project.panos || [], p = list.find((x) => x.id === cur);
  const set = (id, fn) => updateProject((pr) => ({ ...pr, panos: (pr.panos || []).map((x) => (x.id === id ? { ...x, ...fn(x) } : x)) }));
  const add = async (f) => {
    if (!f) return;
    try {
      const src = (await compressImage(f, 4096, 0.82)).data, it = { id: uid('pn'), name: `${t.pnRoom} ${list.length + 1}`, src, marks: [], floor: project.planOn || null, at: Date.now() };
      updateProject((pr) => ({ ...pr, panos: [...(pr.panos || []), it] })); setCur(it.id);
    } catch { toast.error(t.fpFail); }
  };
  const save = () => { if (!txt.trim()) return; set(p.id, (x) => ({ marks: [...(x.marks || []), { id: uid('pm'), lon: pend.lon, lat: pend.lat, text: txt.trim(), at: Date.now() }] })); setPend(null); setTxt(''); };
  return createPortal(
    <div className="editor-backdrop" data-testid="pano-dialog">
      <div className="editor pn-editor">
        <div className="editor-head"><h4><Orbit size={18} /> {t.pnTitle}</h4><button className="icon-btn" data-testid="pano-close-btn" onClick={() => setOn(false)}><X size={16} /></button></div>
        <div className="row">
          {list.map((x) => <button key={x.id} className={`chip seg ${cur === x.id ? 'on' : ''}`} data-testid={`pano-tab-${x.id}`} onClick={() => { setCur(x.id); setPend(null); }}>{x.name}</button>)}
          <button className="btn btn-green btn-sm" data-testid="pano-add-btn" onClick={() => file.current.click()}><Upload size={14} /> {t.pnAdd}</button>
          <input ref={file} type="file" hidden accept="image/*" data-testid="pano-file-input" onChange={(e) => { add(e.target.files[0]); e.target.value = ''; }} />
        </div>
        {!p && <p className="empty" data-testid="pano-empty">{t.pnEmpty}</p>}
        {p && <>
          <div className="row">
            <input className="offer-in" data-testid="pano-name-input" value={p.name} onChange={(e) => set(p.id, () => ({ name: e.target.value }))} />
            <button className="icon-btn danger" data-testid="pano-del-btn" onClick={() => { if (window.confirm(t.moDelQ)) { updateProject((pr) => ({ ...pr, panos: (pr.panos || []).filter((x) => x.id !== p.id) })); setCur(null); } }}><Trash2 size={15} /></button>
          </div>
          <PanoView key={p.id} pano={p} pending={pend} onTap={(d) => { setPend(d); setTxt(''); }} />
          {pend ? <div className="row" data-testid="pano-pending">
            <input className="offer-in" autoFocus data-testid="pano-mark-text" placeholder={t.pnMarkPh} value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} />
            <button className="btn btn-green btn-sm" data-testid="pano-mark-save" onClick={save}><Check size={14} /> OK</button>
            <button className="icon-btn" data-testid="pano-mark-cancel" onClick={() => setPend(null)}><X size={15} /></button>
          </div> : <p className="muted small-hint">{t.pnHint}</p>}
          <ol className="pn-list" data-testid="pano-mark-list">{(p.marks || []).map((mk) => <li key={mk.id}>{mk.text} <button className="icon-btn danger" data-testid={`pano-mark-del-${mk.id}`} onClick={() => set(p.id, (x) => ({ marks: x.marks.filter((y) => y.id !== mk.id) }))}><Trash2 size={12} /></button></li>)}</ol>
        </>}
      </div>
    </div>,
    portalRoot(),
  );
}
